import React, { SyntheticEvent } from 'react';

import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { bemCssModules, getTestId, trimStringMiddle } from '@utils';
import { useCryptoCurrencyName, useCurrencyBase, useNavigate } from '@utils/hooks';

import { operationBlockActions, useStore } from '@modules';
import { CryptoCurrencyEnum } from '@modules/global/types';

import { DataKeysEnum } from '@containers/OperationBlock/types';

import Balance from './Balance';

import style from './Currency.scss';

const cn = bemCssModules(style, 'Currency');
const ti = getTestId('Currency');

export type TCurrency = {
  currency: CryptoCurrencyEnum;
};

const MAX_CHAR_COUNT = 15;

const Currency: CustomFC<TCurrency> = ({ currency, className }) => {
  const { currency: activeCryptoCurrency } = useParams<{ currency: CryptoCurrencyEnum }>();
  const { t } = useTranslation();

  const navigate = useNavigate();
  const [{ wallet, cards }, dispatch] = useStore();
  const { getBaseNetwork } = useCurrencyBase();
  const isActive = activeCryptoCurrency === currency;

  const currencyName = useCryptoCurrencyName(currency);

  const trimmedCurrencyName =
    currencyName.length > MAX_CHAR_COUNT + 1
      ? trimStringMiddle(currencyName, MAX_CHAR_COUNT)
      : currencyName;

  const getBalances = () => {
    const { fiat_balance, fiat_currency, balance } = wallet?.data?.[currency] || {};

    return {
      crypto: { balance, currency },
      fiat: {
        balance: fiat_balance,
        currency: fiat_currency,
      },
    };
  };

  const handleClick = () => {
    navigate(`${CONST.nav.balance}/${currency}`);
  };

  const network = getBaseNetwork(currency);
  const { crypto, fiat } = getBalances();
  const hasBalance = Number(crypto?.balance) > 0;

  const handleClickBuyButton = (e: SyntheticEvent<HTMLDivElement>) => {
    e.stopPropagation();
    const cardValue = cards.list[0]?.id || 'new_card';
    dispatch(operationBlockActions.setLeft({ group: DataKeysEnum.cards, value: cardValue }));
    dispatch(operationBlockActions.setRight({ group: DataKeysEnum.my_wallets, value: currency }));
    navigate(CONST.nav.root);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className={cn({ isActive, hasBalance }, [className])}
      data-testid={ti()}
    >
      <div className={cn('left')}>
        <div className={cn('currency')}>
          <div className={cn('name')}>{trimmedCurrencyName}</div>
          {network && <span className={cn('network')}>{network}</span>}
        </div>
      </div>
      <div className={cn('right')}>
        {hasBalance ? (
          <Balance crypto={crypto} fiat={fiat} />
        ) : (
          <div
            className={cn('topup')}
            role="button"
            tabIndex={0}
            onClick={handleClickBuyButton}
            onKeyDown={handleClickBuyButton}
          >
            {t('public.buy_currency', { currency })}
          </div>
        )}
      </div>
    </button>
  );
};
export default Currency;
